/**
 * PlaneComponent - Handles 2D image/video planes floating in 3D space
 */

import { Logger } from '../utils/Logger';
import { Scene } from '@babylonjs/core/scene';

const logger = Logger.getLogger('PlaneComponent');
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import '@babylonjs/core/Meshes/mesh.vertexData';

// Import shared PlaneConfig from centralized types
import type { PlaneConfig } from '../types';
import { MediaLoader } from '../media/MediaLoader';
import type { MediaItem } from '../media/MediaConfig';

// Re-export for backward compatibility
export type { PlaneConfig };

export class PlaneComponent {
  private scene: Scene;
  private parentNode: TransformNode | null = null;
  private planes: Mesh[] = [];
  private materials: PBRMaterial[] = [];
  private videoElements: HTMLVideoElement[] = [];
  private pendingClickListeners: Array<() => void> = [];
  private mediaLoader: MediaLoader;

  constructor(scene: Scene) {
    this.scene = scene;
    this.mediaLoader = new MediaLoader(scene);
  }

  /**
   * Set the parent node for all plane meshes (for skybox rotation grouping)
   */
  setParentNode(parent: TransformNode | null): void {
    this.parentNode = parent;
    // Re-parent any existing planes
    this.planes.forEach((plane) => {
      plane.parent = parent;
    });
  }

  /**
   * Load multiple plane items
   * FIXED: Uses Promise.allSettled to handle individual plane failures gracefully
   */
  async load(planeConfigs: PlaneConfig[]): Promise<void> {
    logger.info(`Loading ${planeConfigs.length} plane(s)...`);

    // Unload any existing planes first
    this.unload();

    // FIXED: Use Promise.allSettled to handle individual failures gracefully
    // This ensures one failing plane doesn't break all others
    const loadPromises = planeConfigs.map((config, index) => this.loadSinglePlane(config, index));
    const results = await Promise.allSettled(loadPromises);

    // Log any failures but continue with successfully loaded planes
    const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
    if (failures.length > 0) {
      logger.warn(`${failures.length} plane(s) failed to load:`);
      failures.forEach((f, i) => logger.warn(`  - Plane ${i}: ${f.reason}`));
    }

    const successCount = results.filter(r => r.status === 'fulfilled').length;
    logger.info(`${successCount}/${planeConfigs.length} plane(s) loaded successfully`);
  }

  /**
   * Load a single plane
   */
  private async loadSinglePlane(config: PlaneConfig, index: number): Promise<void> {
    try {
      logger.debug(`Loading plane ${index + 1}: ${config.type} from ${config.url}`);

      // Create plane mesh with UNIT geometry (1x1)
      // Actual dimensions are applied via plane.scaling so that
      // getTransform/setTransform read and write a single source of truth.
      // Previously, dimensions were baked into geometry vertices while scaling
      // stayed at (1,1,1), causing getTransform to return wrong values and
      // corrupting the config when any transform field was edited.
      const plane = MeshBuilder.CreatePlane(
        `plane_${index}`,
        {
          width: 1,
          height: 1,
          sideOrientation: Mesh.DOUBLESIDE,
        },
        this.scene
      );

      // Apply actual dimensions via scaling
      plane.scaling.x = config.scale?.width || 2.0;
      plane.scaling.y = config.scale?.height || 1.5;

      // Set position (default: slightly in front and at eye level)
      if (config.position) {
        plane.position = new Vector3(
          config.position.x,
          config.position.y,
          config.position.z
        );
      } else {
        // Default position: centered at eye level, 3 meters away
        plane.position = new Vector3(0, 1.5, -3);
      }

      // Set rotation
      if (config.rotation) {
        plane.rotation = new Vector3(
          config.rotation.x,
          config.rotation.y,
          config.rotation.z
        );
      }

      // Create material (PBR unlit for true-to-source colors)
      const material = new PBRMaterial(`plane_material_${index}`, this.scene);
      material.backFaceCulling = false; // Allow viewing from both sides
      material.unlit = true;  // Unlit mode for accurate texture colors

      // Load texture based on type
      if (config.type === 'video') {
        await this.loadVideoTexture(material, config, index);
      } else {
        await this.loadImageTexture(material, config);
      }

      // Apply material to plane
      plane.material = material;

      // Parent to scene content parent if set (for skybox rotation grouping)
      if (this.parentNode) {
        plane.parent = this.parentNode;
      }

      // Store references
      this.planes.push(plane);
      this.materials.push(material);

      logger.debug(`Plane ${index + 1} loaded successfully at position:`, plane.position);
    } catch (error) {
      logger.error(`Failed to load plane ${index + 1}:`, error);
      throw error;
    }
  }

  /**
   * Load image texture
   */
  private async loadImageTexture(material: PBRMaterial, config: PlaneConfig): Promise<void> {
    return new Promise((resolve, reject) => {
      const texture = new Texture(
        config.url,
        this.scene,
        false, // noMipmap
        true,  // invertY
        Texture.TRILINEAR_SAMPLINGMODE,
        () => {
          logger.debug('Image texture loaded successfully');
          material.albedoTexture = texture;
          material.albedoColor = new Color3(1, 1, 1);
          resolve();
        },
        (message) => {
          logger.error('Error loading image texture:', message);
          reject(new Error(`Failed to load image: ${config.url}`));
        }
      );

      // Enable alpha if image has transparency
      texture.hasAlpha = true;
    });
  }

  /**
   * Load video texture with HLS streaming support
   * Uses MediaLoader.loadStreamingVideo() for adaptive bitrate streaming
   */
  private async loadVideoTexture(material: PBRMaterial, config: PlaneConfig, index: number): Promise<void> {
    // Create MediaItem from PlaneConfig for streaming loader
    const mediaItem: MediaItem = {
      id: `plane_video_${index}`,
      title: `Plane Video ${index}`,
      type: 'video',
      url: config.url,
      hlsUrl: config.hlsUrl, // Use HLS if available
    };

    try {
      // Use streaming video loader (handles HLS with MP4 fallback)
      const videoTexture = await this.mediaLoader.loadStreamingVideo(mediaItem);

      // Store video element reference for playback control
      this.videoElements.push(videoTexture.video);

      // Apply texture to material
      material.albedoTexture = videoTexture;
      material.albedoColor = new Color3(1, 1, 1);

      // Configure video settings
      videoTexture.video.loop = config.loop !== false;
      videoTexture.video.muted = config.muted !== false;

      // Handle autoplay
      if (config.autoplay !== false) {
        try {
          await videoTexture.video.play();
          logger.debug('Video autoplay started');
        } catch (error) {
          logger.warn('Video autoplay blocked. User interaction required.', error);
          // Add click listener to start video on first user interaction
          const playOnClick = async () => {
            try {
              await videoTexture.video.play();
              logger.debug('Video playback started after user interaction');
            } catch (e) {
              logger.error('Failed to play video after user interaction:', e);
            }
            // Remove from pending list
            const idx = this.pendingClickListeners.indexOf(playOnClick);
            if (idx !== -1) {
              this.pendingClickListeners.splice(idx, 1);
            }
          };
          this.pendingClickListeners.push(playOnClick);
          document.addEventListener('click', playOnClick, { once: true });
        }
      }

      logger.debug(`Video texture loaded: ${config.hlsUrl ? 'HLS' : 'Progressive'}`);
    } catch (error) {
      logger.error('Error loading video texture:', error);
      throw new Error(`Failed to load video: ${config.url}`);
    }
  }

  /**
   * Get all video elements (for external control)
   */
  getVideoElements(): HTMLVideoElement[] {
    return this.videoElements;
  }

  /**
   * Play all videos
   */
  async playAllVideos(): Promise<void> {
    const playPromises = this.videoElements.map(async (video) => {
      try {
        if (video.paused) {
          await video.play();
          logger.debug('Video playback resumed');
        }
      } catch (error) {
        logger.error('Failed to play video:', error);
      }
    });

    await Promise.all(playPromises);
  }

  /**
   * Pause all videos
   */
  pauseAllVideos(): void {
    this.videoElements.forEach((video) => {
      if (!video.paused) {
        video.pause();
        logger.debug('Video playback paused');
      }
    });
  }

  /**
   * Get plane meshes (for external manipulation)
   */
  getPlanes(): Mesh[] {
    return this.planes;
  }

  /**
   * Get a specific plane by index
   * @param index Index of the plane
   */
  getPlane(index: number): Mesh | null {
    return this.planes[index] || null;
  }

  /**
   * Get plane count
   */
  getPlaneCount(): number {
    return this.planes.length;
  }

  /**
   * Set the transform of a plane directly without reloading
   * This is efficient for real-time updates from gizmos or input fields
   * @param index Index of the plane to transform
   * @param position New position (optional)
   * @param rotation New rotation in radians (optional)
   * @param scale New scale as width/height (optional)
   */
  setTransform(
    index: number,
    position?: { x: number; y: number; z: number },
    rotation?: { x: number; y: number; z: number },
    scale?: { width: number; height: number }
  ): boolean {
    const plane = this.planes[index];
    if (!plane) {
      logger.warn('Plane not found for transform update at index:', index);
      return false;
    }

    if (position) {
      plane.position.set(position.x, position.y, position.z);
    }

    if (rotation) {
      plane.rotation.set(rotation.x, rotation.y, rotation.z);
    }

    if (scale) {
      // For planes, scaling directly adjusts the mesh dimensions
      plane.scaling.x = scale.width;
      plane.scaling.y = scale.height;
    }

    logger.debug('Transform updated for plane at index:', index);
    return true;
  }

  /**
   * Get the current transform of a plane
   * @param index Index of the plane
   */
  getTransform(index: number): { position: { x: number; y: number; z: number }; rotation: { x: number; y: number; z: number }; scale: { width: number; height: number } } | null {
    const plane = this.planes[index];
    if (!plane) return null;

    return {
      position: { x: plane.position.x, y: plane.position.y, z: plane.position.z },
      rotation: { x: plane.rotation.x, y: plane.rotation.y, z: plane.rotation.z },
      scale: { width: plane.scaling.x, height: plane.scaling.y },
    };
  }

  /**
   * Unload and dispose all planes
   */
  unload(): void {
    logger.debug(`Unloading ${this.planes.length} plane(s)...`);

    // Clean up any pending click listeners to prevent memory leaks
    this.pendingClickListeners.forEach((listener) => {
      document.removeEventListener('click', listener);
    });
    this.pendingClickListeners = [];

    // Pause and clear video elements
    this.videoElements.forEach((video) => {
      video.pause();
      video.src = '';
      video.load();
    });
    this.videoElements = [];

    // Dispose materials and textures
    this.materials.forEach((material) => {
      if (material.albedoTexture) {
        material.albedoTexture.dispose();
      }
      material.dispose();
    });
    this.materials = [];

    // Dispose plane meshes
    this.planes.forEach((plane) => {
      plane.dispose();
    });
    this.planes = [];

    logger.debug('All planes unloaded');
  }

  /**
   * Clear all planes (alias for unload)
   */
  clear(): void {
    this.unload();
  }

  /**
   * Dispose component resources
   */
  dispose(): void {
    this.unload();
  }
}
